class RelatedProducts extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.collectionHandle || !this.dataset.productHandle) {
      return;
    }

    onLibReady(['vue', 'swiper'], this.#init.bind(this));
  }

  #init() {
    new Vue({
      el: this,
      data: {
        collectionHandle: this.dataset.collectionHandle,
        productHandle: this.dataset.productHandle,
        products: [],
        collection: {},
        voltage: Shopify.currentCountry?.voltage ?? Shopify.strings.v110,
        addingToCart: null,
      },
      template: `
        <div class="r-products section" v-if="products?.length > 1">
          <h2 class="section-title" v-if="collection.title">Other from {{ collection.title }}</h2>
  
          <div class="r-products__list">
            <div class="swiper-container" ref="slider">
              <div class="swiper-wrapper">
                <template v-for="product in products">
                  <div class="swiper-slide" :key="product.id">
                    <div class="product-card">
                      <a
                        :href="Shopify.routes.root + 'products/' + product.handle + '?variant=' + product.variant_id"
                        style="position: absolute; inset: 0; z-index: 1;"
                        :aria-label="product.title"
                      ></a>

                      <div class="product-card__img">
                        <div class="product-card__img-wrap">
                          <img 
                            :srcset="formatImage(product.featured_image, '250x250', 'center')"
                            src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                            :alt="product.title"
                            loading="lazy"
                            width="250"
                            height="250"
                          >
                        </div>
                      </div>

                      <div class="product-card__badges">
                        <div class="product__badge product__badge--professional" v-if="product.tags.includes('Professional Edition')">
                          Professional edition
                        </div>

                        <div
                          class="product__badge product__badge--sale"
                          v-if="product.compare_at_price > product.price || product.tags.some(tag => tag.includes('sale_'))"
                        >
                          Special Offer
                        </div>
                      </div>
                      
                      <div class="product-card__information">
                        <p class="product-card__title">
                          {{ product.title }}
                        </p>
        
                        <div class="product-card__rating">
                          <div class="product-item__rating yotpo bottomLine"
                            data-appkey="0ILNinaFxuHqNEhcKA8LxysAhrKBRoZRI9BCcMnf"
                            :data-domain="Shopify.shop"
                            :data-product-id="product.id"
                            :data-product-models="product.id"
                            :data-name="product.title"
                            v-if="product.reviews_count && product.reviews_count > 0"
                          />

                          <div class="yotpo bottomline" v-else>
                            <div class="yotpo-bottomline">
                                <span class="yotpo-stars">
                                  <span class="yotpo-icon yotpo-icon-empty-star pull-left"></span>
                                  <span class="yotpo-icon yotpo-icon-empty-star pull-left"></span>
                                  <span class="yotpo-icon yotpo-icon-empty-star pull-left"></span>
                                  <span class="yotpo-icon yotpo-icon-empty-star pull-left"></span>
                                  <span class="yotpo-icon yotpo-icon-empty-star pull-left"></span>
                                </span>
                                <div class="yotpo-clr"></div>
                                <span class="text-m" aria-label="0 reviews" tabindex="-1">0 Reviews</span>
                            </div>
                          </div>
                        </div>
                        
                        <div class="product-card__price">
                          <div class="price">
                            <span
                              class="compare-at-price"
                              v-html="formatMoney(product.compare_at_price)"
                              v-if="product.compare_at_price > product.price"
                            ></span>

                            <span v-html="formatMoney(product.price)"></span>
                          </div>
                        </div>
                        
                        <div class="product-card__form">
                          <button class="button button--orange button--outline" :class="{'loading': addingToCart === product.id}" @click.prevent="handleAddToCart(product)" aria-label="Add to cart">
                            <span v-html="window.Shopify.icons.bag" />
                            <span>Add to cart</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div class="slider-scrollbar" ref="sliderScrollbar"></div>
            </div>

            <div class="slider-button-prev" ref="sliderBtnPrev" v-html="Shopify.icons.arrowPrev" />
            <div class="slider-button-next" ref="sliderBtnNext" v-html="Shopify.icons.arrowNext" />
          </div>
        </div>
      `,

      mounted() {
        this.setupEventListeners();

        if (Shopify.currentCountry && Shopify.currentCountry.voltage) {
          this.init();
        } else {
          document.addEventListener('countrySelector:ready', () => {
            this.init();
          });
        }
      },

      methods: {
        setupEventListeners() {
          if (!['waffle-mixes', 'oldsoul'].includes(this.collectionHandle)) {
            document.addEventListener('updateVoltage', evt => {
              this.voltage = evt.detail.voltage;
              this.products = this.getProducts();
            });
          }

          Shopify.discount.onUpdated(() => {
            this.products = this.getProducts();
          });
        },

        async init() {
          this.currentProduct = await Shopify.shopifyService.fetchProduct(this.productHandle);
          this.collection = await Shopify.shopifyService.fetchCollection(this.collectionHandle);

          this.products = this.getProducts();

          if (this.currentProduct && this.collection?.products) {
            setTimeout(() => {
              this.initSlider();

              if (window.Yotpo) {
                const api = new Yotpo.API(yotpo);
                api.refreshWidgets();
              }
            }, 0);
          }
        },

        getProducts() {
          if (!this.currentProduct || !this.collection?.products) return [];

          let initialProducts = this.collection.products.filter(product => product.id !== this.currentProduct.id);

          if (['waffle-mixes', 'oldsoul'].includes(this.collectionHandle)) {
            initialProducts = initialProducts
              .sort((a, b) => (a.price < b.price ? 1 : -1))
              .map(product => {
                const isProductEligibleForDiscount = Shopify.discount.isProductEligibleForDiscount(
                  product,
                  this.discount,
                );
                const originalPrice = product.price;

                return {
                  ...product,
                  variant_id: product.variants[0].id,
                  price: isProductEligibleForDiscount
                    ? Shopify.discount.getPriceWithDiscount(originalPrice, this.discount)
                    : originalPrice,
                  compare_at_price: Math.max(product.compare_at_price, originalPrice),
                };
              });
          } else {
            initialProducts = initialProducts
              .flatMap(product => product.variants)
              .sort((a, b) => (a.price < b.price ? 1 : -1))
              .map(variant => {
                const discountCode = Shopify.discount.appliedDiscountCodes.find(code =>
                  Shopify.discount.isProductEligibleForDiscount(
                    initialProducts.find(product => product.id === variant.product_id),
                    code,
                  ),
                );

                const originalPrice = variant.price;

                return {
                  ...variant,
                  price: discountCode
                    ? Shopify.discount.getPriceWithDiscount(originalPrice, discountCode)
                    : originalPrice,
                  compare_at_price: Math.max(variant.compare_at_price, originalPrice),
                  variant_id: variant.id,
                  id: variant.product_id,
                  handle: variant.product_handle,
                  tags: variant.product_tags,
                  title: variant.full_title,
                };
              })
              .filter(
                variant =>
                  !variant.options_title.includes(Shopify.strings.voltage) ||
                  variant.options.includes(this.voltage) ||
                  variant.options.includes(Shopify.strings.na),
              );
          }

          return initialProducts.filter(p => p.available);
        },

        initSlider() {
          if (!this.$refs.slider) {
            return;
          }

          new Swiper(this.$refs.slider, {
            speed: 500,
            slidesPerView: 'auto',
            observer: true,
            watchOverflow: true,
            navigation: {
              nextEl: this.$refs.sliderBtnNext,
              prevEl: this.$refs.sliderBtnPrev,
            },
            scrollbar: {
              el: this.$refs.sliderScrollbar,
              draggable: true,
            },
            breakpoints: {
              750: {
                spaceBetween: 20,
              },
            },
          });
        },

        handleAddToCart(variant) {
          if (this.addingToCart) return;
          this.addingToCart = variant.id;

          window.cartDrawer
            .add({
              items: [
                {
                  id: variant.variant_id,
                  quantity: 1,
                  properties: {
                    DETAILS: getDetails(variant),
                  },
                },
              ],
            })
            .then(() => {
              this.addingToCart = null;
            });
        },
      },
    });
  }
}

customElements.define('related-products', RelatedProducts);
